/**
 * FeaturedService - Service for fetching featured items from Voyager API
 *
 * Fetches items tagged as "featured" from the Voyager search endpoint.
 */
import { logDebug, logError } from 'nsdi-volto-frontend/utils/logger';
import { environment } from 'nsdi-volto-frontend/config/environments';

// API Configuration
export interface FeaturedApiConfig {
  baseUrl: string;
  configId: string;
  rows?: number;
  featuredQuery?: string;
  detailsPageBaseUrl?: string;
}

export const DEFAULT_FEATURED_CONFIG: FeaturedApiConfig = {
  baseUrl: environment.voyager.baseUrl,
  configId: environment.voyager.configId,
  rows: 12,
  featuredQuery: 'tag_tags:featured',
  detailsPageBaseUrl: environment.voyager.detailsUrl,
};

// Response types from Voyager API
// Note: Voyager field aliases like 'thumb:[thumbURL]' return as 'thumb' in the response
export interface VoyagerFeaturedDoc {
  id: string;
  title?: string;
  name?: string;
  format?: string;
  format_type?: string;
  format_category?: string;
  format_app?: string;
  abstract?: string;
  description?: string;
  thumb?: string; // From thumb:[thumbURL] transformation
  path_to_thumb?: string;
  fullpath?: string; // From fullpath:[absolute] transformation
  absolute_path?: string;
  keywords?: string[];
  tag_flags?: string | string[]; // Can be string or array
  likes?: number;
  dislikes?: number;
  bytes?: number;
  modified?: string;
  views?: number;
  fs_event?: string;
  fs_platform?: string;
}

export interface VoyagerFeaturedResponse {
  response: {
    numFound: number;
    start: number;
    docs: VoyagerFeaturedDoc[];
  };
}

// Normalized featured item for UI
export interface FeaturedItemData {
  id: string;
  title: string;
  description?: string;
  thumbnail?: string;
  format?: string;
  formatType?: string;
  formatApp?: string;
  link?: string;
  tags?: string[];
  keywords?: string[];
  likes?: number;
  dislikes?: number;
  fileSize?: string;
  views?: number;
  event?: string;
  platform?: string;
}

/**
 * Build the featured items query URL
 */
function buildFeaturedQueryUrl(config: FeaturedApiConfig): string {
  // Clean URL - remove trailing slash and /solr/v0/select if present
  const baseUrl = config.baseUrl
    .replace(/\/$/, '')
    .replace(/\/solr\/v0\/select$/, '');
  const rows = config.rows || 48;
  const featuredQuery = config.featuredQuery || 'tag_tags:featured';

  // Check if featuredQuery is a full query string (contains =) or a simple filter
  const isFullQueryString = featuredQuery.includes('=');

  // Fields to fetch
  const fields = [
    'id',
    'title',
    'name',
    'name:[name]',
    'format',
    'abstract',
    'fullpath:[absolute]',
    'absolute_path:[absolute]',
    'thumb:[thumbURL]',
    'path_to_thumb',
    'subject',
    'download:[downloadURL]',
    'format_type',
    'bytes',
    'modified',
    'shard:[shard]',
    'bbox',
    'geo:[geo]',
    'format_category',
    'component_files',
    'ags_fused_cache',
    'linkcount__children',
    'contains_name',
    'wms_layer_name',
    'tag_flags',
    'hasMissingData',
    'layerURL:[lyrURL]',
    'hasLayerFile',
    'likes',
    'dislikes',
    'description',
    'keywords',
    'name_alias',
    'fd_publish_date',
    'fs_event',
    'fd__acquired',
    'fs_platform',
    'fs__provider',
    'fd_acquired',
    'fs__satellite_id',
    'rasterSize',
    'format_app',
  ].join(',');

  // Build base params
  const params = new URLSearchParams({
    start: '0',
    fl: fields,
    rows: rows.toString(),
    'extent.bbox': 'true',
    block: 'true',
    'voyager.config.id': config.configId,
    wt: 'json',
  });

  if (isFullQueryString) {
    // Parse the full query string and merge parameters
    // The user's query params take precedence over defaults
    const userParams = new URLSearchParams(featuredQuery);

    // Set defaults only if not provided in user query
    if (!userParams.has('sort')) {
      params.set('sort', 'fi_score desc');
    }
    if (!userParams.has('disp')) {
      params.set('disp', config.configId);
    }
    if (!userParams.has('basemap')) {
      params.set('basemap', 'ESRI Street Map');
    }

    // Merge user params into our params (user params override)
    userParams.forEach((value, key) => {
      params.set(key, value);
    });
  } else {
    // Simple filter query - use as fq parameter
    params.set('sort', 'fi_score desc');
    params.set('disp', config.configId);
    params.set('basemap', 'ESRI Street Map');
    params.set('fq', featuredQuery);
  }

  return `${baseUrl}/solr/v0/select?${params.toString()}`;
}

/**
 * Fetch featured items from Voyager API
 */
export async function fetchFeaturedItems(
  config: FeaturedApiConfig = DEFAULT_FEATURED_CONFIG,
): Promise<FeaturedItemData[]> {
  const url = buildFeaturedQueryUrl(config);

  logDebug('[FeaturedService] Fetching featured items from:', url);

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    logDebug('[FeaturedService] Response status:', response.status);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data: VoyagerFeaturedResponse = await response.json();

    logDebug(
      '[FeaturedService] Found',
      data.response.numFound,
      'featured items',
    );
    logDebug('[FeaturedService] First doc:', data.response.docs[0]);

    // Transform API response to our normalized format
    return data.response.docs.map((doc) => transformVoyagerDoc(doc, config));
  } catch (error) {
    logError('FeaturedService', 'Failed to fetch featured items', error);
    throw error;
  }
}

/**
 * Format bytes to human readable string
 */
function formatBytes(bytes?: number): string | undefined {
  if (bytes === undefined || bytes === null) return undefined;
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'kB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Transform Voyager doc to FeaturedItemData
 */
function transformVoyagerDoc(
  doc: VoyagerFeaturedDoc,
  config: FeaturedApiConfig,
): FeaturedItemData {
  // Get the best available title (empty string if none available)
  const title = doc.title || doc.name || '';

  // Get description from abstract or description field
  const description = doc.abstract || doc.description || '';

  // Build thumbnail URL
  // Use the 'thumb' field first (full URL from [thumbURL] transformation)
  // Fall back to constructing from path_to_thumb
  let thumbnail = '';

  logDebug('[FeaturedService] Doc ID:', doc.id);
  logDebug('[FeaturedService] Raw doc.thumb:', doc.thumb);
  logDebug('[FeaturedService] Raw doc.path_to_thumb:', doc.path_to_thumb);

  if (
    doc.thumb &&
    typeof doc.thumb === 'string' &&
    doc.thumb.startsWith('http')
  ) {
    thumbnail = doc.thumb;
  } else if (doc.path_to_thumb) {
    // Construct thumbnail URL from path_to_thumb
    const baseUrl = config.baseUrl.replace(/\/$/, '');
    thumbnail = `${baseUrl}/api/rest/index/thumb/${doc.path_to_thumb}`;
  }

  logDebug('[FeaturedService] Final thumbnail URL:', thumbnail);

  // Build tags from tag_flags or keywords
  // tag_flags can be a string or array
  const tags: string[] = [];
  if (doc.tag_flags) {
    if (typeof doc.tag_flags === 'string') {
      // Single string - use as-is
      if (doc.tag_flags !== 'featured') {
        tags.push(doc.tag_flags);
      }
    } else if (Array.isArray(doc.tag_flags)) {
      // Array of strings
      tags.push(...doc.tag_flags.filter((t) => t !== 'featured'));
    }
  }

  // Build item link using detailsPageBaseUrl
  const detailsBase =
    config.detailsPageBaseUrl ||
    DEFAULT_FEATURED_CONFIG.detailsPageBaseUrl ||
    '/search';
  const link = `${detailsBase}?id=${encodeURIComponent(doc.id)}`;

  return {
    id: doc.id,
    title,
    description: description || undefined,
    thumbnail: thumbnail || undefined,
    format: doc.format || undefined,
    formatType: doc.format_type || doc.format_category || undefined,
    formatApp: doc.format_app || undefined,
    link,
    tags: tags.length > 0 ? tags : undefined,
    keywords:
      doc.keywords && doc.keywords.length > 0 ? doc.keywords : undefined,
    likes: doc.likes,
    dislikes: doc.dislikes,
    fileSize: formatBytes(doc.bytes),
    views: doc.views,
    event: doc.fs_event || undefined,
    platform: doc.fs_platform || undefined,
  };
}

const FeaturedService = {
  fetchFeaturedItems,
  DEFAULT_FEATURED_CONFIG,
};
export default FeaturedService;
